import React, { useMemo, useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import moment from "moment";
import { Modal, Form, Input, InputNumber, Select, Row, Col, message } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteOnboardingRequest,
  updateOnboardingRequest,
} from "../../redux/onboardingSlice";
import { formatPrice, getCurrencySymbol } from "../../utils/currency";
import "./ag-grid-custom.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const PLATFORM_OPTIONS = [
  { label: "Ghost", value: "Ghost" },
  { label: "beehiiv", value: "beehiiv" },
  { label: "WordPress", value: "WordPress" },
];

const OnboardingRequestsDataTableAG = ({ requests, loading }) => {
  const [modal, contextHolder] = Modal.useModal();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [form] = Form.useForm();
  const [messageApi, messageContextHolder] = message.useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    (record) => {
      modal.confirm({
        title: "Are you sure you want to delete this onboarding request?",
        content: `${record.name} (${record.email})`,
        onOk: async () => {
          await dispatch(deleteOnboardingRequest({ id: record.id }));
        },
      });
    },
    [modal, dispatch]
  );

  const handleView = useCallback(
    (record) => {
      setViewRecord(record);
      form.setFieldsValue({
        platform: record.platform,
        name: record.name,
        email: record.email,
        website: record.website,
        country: record.country,
        currency: record.currency,
        pricePerArticle: record.pricePerArticle,
        referredBy: record.referredBy,
        ghostAdminApiKey: record.ghostAdminApiKey,
        ghostApiUrl: record.ghostApiUrl,
        beehiivPublicationId: record.beehiivPublicationId,
        beehiivApiKey: record.beehiivApiKey,
      });
      setViewModalOpen(true);
    },
    [form]
  );

  const handleViewSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      await dispatch(
        updateOnboardingRequest({ id: viewRecord.id, data: values })
      );
      setViewModalOpen(false);
      setViewRecord(null);
    } catch (err) {
      // validation error
    }
  }, [form, dispatch, viewRecord]);

  const handleAdd = useCallback(
    (record) => {
      navigate("/publisher/add", { state: { onboardingRequest: record } });
    },
    [navigate]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "createdAt",
        headerName: "Date Requested",
        width: 150,
        pinned: "left",
        filter: "agDateColumnFilter",
        sortable: true,
        sort: "desc",
        valueGetter: (params) =>
          params.data?.createdAt ? new Date(params.data.createdAt) : null,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("l LT") : "",
        comparator: (a, b) => {
          if (!a && !b) return 0;
          if (!a) return -1;
          if (!b) return 1;
          return new Date(a) - new Date(b);
        },
      },
      {
        field: "platform",
        headerName: "CMS",
        width: 120,
        pinned: "left",
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "name",
        headerName: "Name",
        width: 160,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "email",
        headerName: "Email",
        width: 220,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "website",
        headerName: "Website",
        width: 220,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "country",
        headerName: "Country",
        width: 140,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 110,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "pricePerArticle",
        headerName: "Price / Article",
        width: 130,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueFormatter: (params) => {
          if (params.value == null) return "";
          const cur = params.data?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "referredBy",
        headerName: "Referred By",
        width: 150,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "ghostAdminApiKey",
        headerName: "Ghost Admin API Key",
        width: 200,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "ghostApiUrl",
        headerName: "Ghost API URL",
        width: 220,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "beehiivPublicationId",
        headerName: "beehiiv Publication ID",
        width: 200,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "beehiivApiKey",
        headerName: "beehiiv API Key",
        width: 200,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 180,
        pinned: "right",
        filter: false,
        sortable: false,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return null;
          return (
            <div style={{ whiteSpace: "normal", lineHeight: "1.5" }}>
              <span
                onClick={() => handleView(record)}
                style={{
                  cursor: "pointer",
                  color: "#1677ff",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              >
                [view]
              </span>
              <span
                onClick={() => handleAdd(record)}
                style={{
                  cursor: "pointer",
                  color: "#1677ff",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              >
                [add]
              </span>
              <span
                onClick={() => handleDelete(record)}
                style={{
                  cursor: "pointer",
                  color: "#1677ff",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              >
                [delete]
              </span>
            </div>
          );
        },
        autoHeight: true,
        wrapText: true,
      },
    ],
    [handleView, handleAdd, handleDelete]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
    }),
    []
  );

  const rowData = useMemo(() => requests || [], [requests]);

  const platform = Form.useWatch("platform", form);

  return (
    <>
      {contextHolder}
      {messageContextHolder}
      <div
        className="ag-theme-alpine"
        style={{ height: "700px", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          pagination={true}
          paginationPageSize={50}
          suppressMenuHide={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
        />
      </div>

      <Modal
        title="View / Edit Onboarding Request"
        open={viewModalOpen}
        onOk={handleViewSave}
        onCancel={() => {
          setViewModalOpen(false);
          setViewRecord(null);
        }}
        okText="Save"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="platform" label="CMS Platform">
                <Select options={PLATFORM_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Name">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="website" label="Website">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="country" label="Country">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Currency">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="pricePerArticle" label="Article Price">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="referredBy" label="Referred By">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          {(platform === "Ghost" || viewRecord?.platform === "Ghost") &&
            platform !== "beehiiv" &&
            platform !== "WordPress" && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="ghostAdminApiKey" label="Ghost Admin API Key">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="ghostApiUrl" label="Ghost API URL">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            )}
          {(platform === "beehiiv" || viewRecord?.platform === "beehiiv") &&
            platform !== "Ghost" &&
            platform !== "WordPress" && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="beehiivApiKey" label="beehiiv API Key">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="beehiivPublicationId"
                    label="beehiiv Publication ID"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            )}
        </Form>
      </Modal>
    </>
  );
};

export default OnboardingRequestsDataTableAG;
